import { toast } from 'react-toastify';
import { useAPI } from 'src/hooks';
import { useDebouncedCallback } from 'use-debounce';
import { useForm } from 'react-hook-form';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Agreement from '@com/modals/Agreement';
import loadable from '@loadable/component';

const ErrorMsg = loadable(() => import('@com/utils/ErrorMsg'));
const Footer = loadable(() => import('@com/footer/Footer'));
const FullSectionText = loadable(() => import('@com/FullSectionText'));
const Header = loadable(() => import('@com/header/Header'));
const TopSection = loadable(() => import('@com/TopSection'));

interface FormInputs {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
}

function Register(): JSX.Element {
  /* Define state / hook */
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [isChecked, setIsChecked] = useState(false);
  const { emailRegister, isUsernameValid } = useAPI();
  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<FormInputs>({ mode: 'onBlur' });
  const { t } = useTranslation();

  /* Custom Data Object for render or other usages */
  const MIN_PASSWORD_LENGTH = 8;

  /* Render control function / logic */
  const onSubmit = useDebouncedCallback(
    async (data: FormInputs) => {
      if (!isChecked) {
        toast.info(t('toastMessage.checkTerms'));
        return;
      }

      const response = await emailRegister({
        name: data.name,
        email: data.email,
        password: data.password,
        confirmPassword: data.confirmPassword,
      });
      if (response.token) setIsSuccess(true);
    },
    500,
    { leading: true, trailing: false }
  );

  /* Data Change Listeners - useEffect/useMemo/useCallback */

  /* Styling */

  /* Render */

  return (
    <>
      <Header />
      <TopSection
        sectionName={t('signUpPage.signUpText', {
          name: process.env.REACT_APP_SHORT_TITLE,
        })}
      />
      {!isSuccess ? (
        <section className="tf-login tf-section content-container">
          <div className="themesflat-container">
            <div className="px-5">
              <div className="flat-form box-register-email">
                <div className="form-inner">
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <input
                      aria-required
                      aria-label={t('signUpPage.yourFullName')}
                      autoComplete="name"
                      data-test="name-input"
                      placeholder={t('signUpPage.yourFullName')}
                      type="text"
                      {...register('name', {
                        required: {
                          value: true,
                          message: t('errorMsg.register.nameRequired'),
                        },
                        pattern: {
                          value: /^[\w-]([\s]?[\w-])*$/i,
                          message: t('errorMsg.editProfile.namePattern'),
                        },
                        validate: {
                          value: async (value) => {
                            return (
                              (await isUsernameValid(value)) ||
                              t('errorMsg.editProfile.nameRepeated')
                            );
                          },
                        },
                      })}
                    />
                    <ErrorMsg errors={errors} name="name" />
                    <input
                      aria-required
                      aria-label={t('signUpPage.yourEmailAddress')}
                      autoComplete="email"
                      data-test="email-input"
                      placeholder={t('signUpPage.yourEmailAddress')}
                      type="text"
                      {...register('email', {
                        required: {
                          value: true,
                          message: t('errorMsg.register.emailRequired'),
                        },
                        pattern: {
                          value:
                            /^\s*[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\s*$/i,
                          message: t('errorMsg.register.emailPattern'),
                        },
                      })}
                    />
                    <ErrorMsg errors={errors} name="email" />
                    <input
                      aria-required
                      aria-label={t('signUpPage.setYourPassword')}
                      autoComplete="new-password"
                      data-test="password-input"
                      placeholder={t('signUpPage.setYourPassword')}
                      type="password"
                      {...register('password', {
                        required: {
                          value: true,
                          message: t('errorMsg.register.passwordRequired'),
                        },
                        minLength: {
                          value: MIN_PASSWORD_LENGTH,
                          message: t('errorMsg.register.passwordMinLength', {
                            length: MIN_PASSWORD_LENGTH,
                          }),
                        },
                      })}
                    />
                    <ErrorMsg errors={errors} name="password" />
                    <input
                      aria-required
                      aria-label="confirmPassword"
                      autoComplete="new-password"
                      data-test="confirm-password-input"
                      placeholder={t('signUpPage.confirmYourPassword')}
                      type="password"
                      {...register('confirmPassword', {
                        required: {
                          value: true,
                          message: t('errorMsg.register.passwordRequired'),
                        },
                        minLength: {
                          value: MIN_PASSWORD_LENGTH,
                          message: t('errorMsg.login.passwordMinLength', {
                            length: MIN_PASSWORD_LENGTH,
                          }),
                        },
                        validate: {
                          value: (value) =>
                            value === getValues('password') ||
                            t('errorMsg.register.passwordUnmatch'),
                        },
                      })}
                    />
                    <ErrorMsg errors={errors} name="confirmPassword" />

                    <div className="mt-5">
                      <Agreement
                        isChecked={isChecked}
                        setIsChecked={setIsChecked}
                      />
                    </div>
                    <button
                      className="mt-5"
                      data-test="register-submit-btn"
                      type="submit"
                    >
                      {t('signUpPage.signUp')}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>
      ) : (
        <FullSectionText>
          <>
            {t('signUpPage.validationEmailSent')}
            <br />
            <br />
            <h3>{getValues('email')}</h3>
            <br />
            <div> {t('signUpPage.pleaseCheckEmail')}</div>
          </>
        </FullSectionText>
      )}

      <Footer />
    </>
  );
}

export default Register;
